'use client';

import FadeIn from '@/components/FadeIn/FadeIn';
import Spinner from '@/components/Spinner';
import { Button } from '@/components/ui/button';
import { FEEDBACK_DATA } from '@/constants/home';
import { FeedbackCard } from '@/views/home/components/B2BLeaders';
import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Clock4,
  Dot,
  SquareMenu,
  TriangleAlert,
} from 'lucide-react';
import Image from 'next/image';
import {
  FC, FormEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { usePostHog } from 'posthog-js/react';
import { ITurnstileElement, Turnstile } from '@/components/Turnstile';
import { Mail } from 'lucide-react';
import { getDemoIntent, withDemoIntent } from './demoIntent';
import {
  BUSINESS_MODEL_OPTIONS,
  FUNDING_STAGE_OPTIONS,
  SALES_TEAM_SIZE_OPTIONS,
  isPlausibleWebsite,
  websiteFromEmail,
} from './leadFields';

dayjs.extend(utc);
dayjs.extend(timezone);

// IMPORTANT: Hardcoded to production vendor ID. Do NOT use NEXT_PUBLIC_* env
// vars here — they are baked at build time and the CI pipeline may not pass them.
// See: GH hotfix 2026-04-21 (18 lost demo leads from incorrect vendor 2730).
const VENDOR_ID = 2815;
const BOOK_DEMO_API = '/api/book-demo';

// Shown when /qualify itself fails. There is no client-side fallback capture: the
// browser used to post the lead to a HubSpot form that did not exist (#4461), so the
// only place a lead is ever recorded is the backend, and when that call fails the
// visitor has to be told so.
const SUBMIT_FAILED_MESSAGE = 'Failed to submit. Please email us at sales@marketbetter.ai';
// Shown when /qualify succeeded but loading the calendar threw. The backend already
// holds the lead at that point, so "Failed to submit" would be wrong and would invite
// a resubmission, i.e. a duplicate lead.
const CALENDAR_FAILED_MESSAGE = 'We received your request, but could not load available times. Please try again or email us at sales@marketbetter.ai';

const EMPLOYEE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '500+'];

const INFORMATION_LIST = [
  { title: '100K+', description: 'Outreach sequences optimized every month' },
  { title: '70%', description: 'Less manual work as AI handles repetitive tasks' },
  { title: '3X', description: 'More meetings booked with AI-powered scheduling.' },
  { title: '90%', description: 'Customers exceeded sales targets within 90 days.' },
];

type Screen = 'LOADING' | 'LEAD_FORM' | 'QUALIFYING' | 'CALENDAR' | 'BOOKING' | 'SUCCESS' | 'NOT_QUALIFIED' | 'ERROR';

interface LeadFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  numEmployees: string;
  jobTitle: string;
  website: string;
  businessModel: string;
  fundingStage: string;
  salesTeamSize: string;
}

interface SalesRep {
  id: string;
  name: string;
  email: string;
  profilePictureUrl?: string;
}

interface CalendarSettings {
  authId: string;
  provider: string;
  meetingDuration: number;
}

interface TimeSlot {
  start: string;
  end: string;
}

/**
 * #3361: resolve a naive slot string (`2026-06-23T12:30:00`, no offset) to the
 * real instant it represents, using the zone the host's calendar emitted it in.
 * Falls back to a plain browser-local parse when no host zone is available, so
 * an older backend degrades to exactly the pre-#3361 behavior rather than to
 * something new.
 */
const parseSlot = (value: string, hostTimezone?: string) => (
  hostTimezone ? dayjs.tz(value, hostTimezone) : dayjs(value)
);

/**
 * #3361: the real instant a slot represents, re-expressed as wall-clock
 * date/time in the visitor's own zone. This is what must be displayed to the
 * visitor and submitted to `/book` — using `parseSlot`'s result directly would
 * display the host's own local time, not the visitor's.
 */
const toVisitorInstant = (value: string, hostTimezone: string | undefined, visitorTimezone: string) => (
  parseSlot(value, hostTimezone).tz(visitorTimezone)
);

// Exported for slotTimezone.test.ts — keeping one implementation rather than a mirrored copy.
export { parseSlot, toVisitorInstant };

// ─── Lead Qualification Form ────────────────────────────────────────────────

const leadInputClass = (error?: string) => clsx(
  'w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white',
  error ? 'border-red-500' : 'border-gray-300',
);

const LeadSelect: FC<{
  label: string;
  value: string;
  options: readonly string[];
  error?: string;
  onChange: (value: string) => void;
}> = ({
  label, value, options, error, onChange,
}) => (
  <div>
    <label className='block text-sm font-medium text-gray-700 mb-1'>{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className={leadInputClass(error)}>
      <option value=''>Select...</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
    {error && <p className='text-xs text-red-500 mt-1'>{error}</p>}
  </div>
);

const LeadForm: FC<{
  onSubmit: (data: LeadFormData) => void;
  isLoading: boolean;
  // Rendered just above the submit button (used to host the Turnstile widget).
  footer?: ReactNode;
  // When true, the submit button is disabled (e.g. until Turnstile resolves).
  submitDisabled?: boolean;
}> = ({
  onSubmit, isLoading, footer, submitDisabled,
}) => {
  const [form, setForm] = useState<LeadFormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    numEmployees: '',
    jobTitle: '',
    website: '',
    businessModel: '',
    fundingStage: '',
    salesTeamSize: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof LeadFormData, string>>>({});
  // The website follows the email's domain until the visitor types their own.
  const [websiteEdited, setWebsiteEdited] = useState(false);

  const handleChange = (field: keyof LeadFormData, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'email' && !websiteEdited ? { website: websiteFromEmail(value) } : {}),
    }));
    if (field === 'website') setWebsiteEdited(true);
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof LeadFormData, string>> = {};
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Invalid email format';
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!form.company.trim()) newErrors.company = 'Company name is required';
    if (!form.website.trim()) newErrors.website = 'Company website is required';
    else if (!isPlausibleWebsite(form.website)) newErrors.website = 'Enter a website like acme.com';
    if (!form.numEmployees) newErrors.numEmployees = 'Select a company size';
    if (!form.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';
    if (!form.businessModel) newErrors.businessModel = 'Select what your company sells';
    if (!form.salesTeamSize) newErrors.salesTeamSize = 'Select your sales team size';
    if (!form.fundingStage) newErrors.fundingStage = 'Select a funding stage';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>First Name *</label>
          <input
            type='text'
            value={form.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            className={clsx(
              'w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
              errors.firstName ? 'border-red-500' : 'border-gray-300',
            )}
            placeholder='First name'
          />
          {errors.firstName && <p className='text-xs text-red-500 mt-1'>{errors.firstName}</p>}
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>Last Name *</label>
          <input
            type='text'
            value={form.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            className={clsx(
              'w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
              errors.lastName ? 'border-red-500' : 'border-gray-300',
            )}
            placeholder='Last name'
          />
          {errors.lastName && <p className='text-xs text-red-500 mt-1'>{errors.lastName}</p>}
        </div>
      </div>

      <div>
        <label className='block text-sm font-medium text-gray-700 mb-1'>Email *</label>
        <input
          type='email'
          value={form.email}
          onChange={(e) => handleChange('email', e.target.value)}
          className={clsx(
            'w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
            errors.email ? 'border-red-500' : 'border-gray-300',
          )}
          placeholder='you@company.com'
        />
        {errors.email && <p className='text-xs text-red-500 mt-1'>{errors.email}</p>}
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>Company Name *</label>
          <input
            type='text'
            value={form.company}
            onChange={(e) => handleChange('company', e.target.value)}
            className={leadInputClass(errors.company)}
            placeholder='Company name'
          />
          {errors.company && <p className='text-xs text-red-500 mt-1'>{errors.company}</p>}
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>Company Website *</label>
          <input
            type='text'
            inputMode='url'
            value={form.website}
            onChange={(e) => handleChange('website', e.target.value)}
            className={leadInputClass(errors.website)}
            placeholder='acme.com'
          />
          {errors.website && <p className='text-xs text-red-500 mt-1'>{errors.website}</p>}
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
        <LeadSelect
          label='Number of Employees *'
          value={form.numEmployees}
          options={EMPLOYEE_OPTIONS}
          error={errors.numEmployees}
          onChange={(value) => handleChange('numEmployees', value)}
        />
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>Job Title *</label>
          <input
            type='text'
            value={form.jobTitle}
            onChange={(e) => handleChange('jobTitle', e.target.value)}
            className={leadInputClass(errors.jobTitle)}
            placeholder='Your role'
          />
          {errors.jobTitle && <p className='text-xs text-red-500 mt-1'>{errors.jobTitle}</p>}
        </div>
      </div>

      <LeadSelect
        label='What does your company sell? *'
        value={form.businessModel}
        options={BUSINESS_MODEL_OPTIONS}
        error={errors.businessModel}
        onChange={(value) => handleChange('businessModel', value)}
      />

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
        <LeadSelect
          label='Sales Team Size (AEs + SDRs) *'
          value={form.salesTeamSize}
          options={SALES_TEAM_SIZE_OPTIONS}
          error={errors.salesTeamSize}
          onChange={(value) => handleChange('salesTeamSize', value)}
        />
        <LeadSelect
          label='Funding Stage *'
          value={form.fundingStage}
          options={FUNDING_STAGE_OPTIONS}
          error={errors.fundingStage}
          onChange={(value) => handleChange('fundingStage', value)}
        />
      </div>

      {footer}

      <Button type='submit' className='w-full mt-2' disabled={isLoading || submitDisabled}>
        {isLoading ? <Spinner className='w-5 h-5' /> : 'Get Started'}
      </Button>
    </form>
  );
};

// ─── Calendar / Time Slot Picker ────────────────────────────────────────────

const CalendarPicker: FC<{
  slots: TimeSlot[];
  onSelectSlot: (slot: TimeSlot) => void;
  isLoading: boolean;
  /**
   * #3361: the zone the slot strings are wall-clock in. They arrive naive
   * (`2026-06-23T12:30:00`, no offset) because Graph's per-slot `timeZone` is
   * dropped server-side, so parsing them as browser-local shifts every slot by
   * the visitor's offset — onto the wrong day, or behind `now` and filtered out
   * entirely, which is what produced "No available slots" while slots existed.
   */
  hostTimezone?: string;
}> = ({ slots, onSelectSlot, isLoading, hostTimezone }) => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const userTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const getWeekDates = useCallback((offset: number) => {
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + offset * 7);
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);
      return day;
    });
  }, []);

  const [lastRefreshedDate, setLastRefreshedDate] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setLastRefreshedDate(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  const weekDates = useMemo(() => getWeekDates(weekOffset), [getWeekDates, weekOffset]);
  const today = useMemo(() => new Date(), []);

  const resolveSlot = useCallback(
    (value: string) => parseSlot(value, hostTimezone),
    [hostTimezone],
  );

  const filteredSlots = useMemo(() => {
    const dateStr = dayjs(selectedDate).format('YYYY-MM-DD');
    const nowLocal = dayjs(lastRefreshedDate);
    // Compare real instants. `isAfter` against a mis-parsed local time is what
    // silently dropped every still-valid slot for a visitor west of the host.
    return slots.filter((slot) => resolveSlot(slot.start).local().format('YYYY-MM-DD') === dateStr
      && resolveSlot(slot.start).isAfter(nowLocal));
  }, [slots, selectedDate, lastRefreshedDate, resolveSlot]);

  if (isLoading) {
    return (
      <div className='flex flex-col items-center justify-center py-16'>
        <Spinner className='w-8 h-8' />
        <p className='text-sm text-gray-500 mt-4'>Loading available times...</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-8'>
      <div className='flex flex-col gap-4 w-[90%] mx-auto'>
        <p className='font-medium text-center'>What day is best for you?</p>
        <div className='flex items-center gap-4 w-full'>
          <Button
            className='p-0.5 h-fit'
            variant='outline'
            onClick={() => setWeekOffset(Math.max(0, weekOffset - 1))}
            disabled={weekOffset === 0}
          >
            <ChevronLeft size={20} />
          </Button>
          <div className='flex-1 border border-gray-200 rounded-2xl cursor-pointer'>
            <div className='bg-gray-100 border-b border-gray-200 flex items-center py-2 rounded-t-2xl'>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className='flex-1 text-base text-gray-600 text-center'>
                  {day}
                </div>
              ))}
            </div>
            <div className='bg-white flex items-center rounded-b-2xl'>
              {weekDates.map((date, index) => {
                const isPast = date < today && date.toDateString() !== today.toDateString();
                const isSelected = date.toDateString() === selectedDate.toDateString();
                return (
                  <div
                    key={index}
                    className={clsx(
                      'flex-1 flex flex-col gap-1 items-center transition-all duration-300 py-4',
                      {
                        'rounded-bl-2xl': index === 0,
                        'rounded-br-2xl': index === 6,
                        'hover:bg-gray-100': !isPast && !isSelected,
                        'bg-primary-500 text-white hover:bg-primary-400': isSelected,
                        'opacity-40 cursor-not-allowed': isPast,
                      },
                    )}
                    onClick={() => !isPast && setSelectedDate(date)}
                  >
                    <p className={clsx('font-medium', isPast && 'text-gray-400')}>{date.getDate()}</p>
                    <p
                      className={clsx(
                        'text-base',
                        isPast ? 'text-gray-400' : 'text-gray-600',
                        isSelected && 'text-white',
                      )}
                    >
                      {dayjs(date).format('MMM')}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
          <Button
            className='p-0.5 h-fit'
            variant='outline'
            onClick={() => setWeekOffset(Math.min(2, weekOffset + 1))}
            disabled={weekOffset >= 2}
          >
            <ChevronRight size={20} />
          </Button>
        </div>
      </div>

      <div className='flex flex-col items-center w-3/5 mx-auto'>
        <p className='font-medium text-center'>What time works?</p>
        <p className='text-sm text-center text-gray-500 flex items-center mx-auto'>
          30 minute meeting <Dot size={28} /> {userTimezone} ({dayjs().format('hh:mm A')})
        </p>
        {filteredSlots.length === 0 ? (
          <p className='text-sm text-gray-400 mt-6'>No available slots for this day</p>
        ) : (
          <div className='grid grid-cols-3 gap-2 w-full pt-6'>
            {filteredSlots.map((slot, index) => (
              <div
                key={index}
                className='transition-all duration-300 border border-gray-200 rounded-2xl p-3 font-medium text-center cursor-pointer hover:bg-gray-100 text-sm hover:shadow-lg bg-white'
                onClick={() => onSelectSlot(slot)}
              >
                {resolveSlot(slot.start).local().format('h:mm A')}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Success Screen ─────────────────────────────────────────────────────────

const SuccessScreen: FC<{
  salesRep: SalesRep;
  slot: TimeSlot;
  onReschedule: () => void;
  // #3361: the zone `slot` is wall-clock in — see toVisitorInstant.
  hostTimezone?: string;
}> = ({
  salesRep, slot, onReschedule, hostTimezone,
}) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const initials = salesRep.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  return (
    <div className='flex flex-col items-center'>
      <div className='w-15 h-15 rounded-lg bg-primary-500 text-white flex justify-center items-center relative text-lg font-semibold'>
        {initials}
        <CircleCheck
          className='text-green-600 bg-white rounded-full p-0.5 absolute -right-1/2 -translate-x-1/2 -bottom-1/2 -translate-y-1/2'
          size={32}
        />
      </div>
      <p className='text-sm text-gray-600 mb-1 mt-8'>You are meeting with</p>
      <p className='font-semibold'>{salesRep.name}</p>
      <p className='text-center py-6 rounded-lg text-green-700 bg-green-100 font-medium mt-8 w-full'>
        An email has been sent with the full booking details
      </p>

      <div className='mt-4 p-5 border border-gray-200 rounded-lg w-full'>
        <div className='flex items-center justify-between gap-6'>
          <div className='flex items-center gap-2'>
            <Clock4 className='p-2 rounded-lg bg-gray-500 text-white' size={40} />
            <p className='text-sm font-semibold'>Time</p>
          </div>
          <p className='flex-1 text-sm'>
            {/* #3361: the booking submit now sends the visitor-zone wall-clock
                equivalent of the real (host-zone-resolved) instant, so this
                display must resolve the same way to keep matching what was
                actually booked. */}
            {toVisitorInstant(slot.start, hostTimezone, userTimezone).format('ddd, MMM DD YYYY')},{' '}
            {toVisitorInstant(slot.start, hostTimezone, userTimezone).format('h:mm A')} - {toVisitorInstant(slot.end, hostTimezone, userTimezone).format('h:mm A')}{' '}
            {userTimezone}
          </p>
          <Button
            className='p-0 h-fit underline font-medium text-primary-600 hover:text-primary-500'
            variant='ghost'
            onClick={onReschedule}
          >
            Reschedule
          </Button>
        </div>
        <div className='h-[1px] w-full bg-gray-200 my-6' />
        <div className='flex items-center gap-6'>
          <div className='flex items-center gap-2'>
            <SquareMenu className='p-2 rounded-lg bg-gray-500 text-white' size={40} />
            <p className='text-sm font-semibold'>Details</p>
          </div>
          <p className='flex-1 text-sm'>We&apos;ve sent an email with your full booking details</p>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page Component ────────────────────────────────────────────────────

const BookDemo: FC = () => {
  const posthog = usePostHog();
  const demoIntent = useMemo(
    () => getDemoIntent(typeof window === 'undefined' ? '' : window.location.search),
    [],
  );
  const [screen, setScreen] = useState<Screen>('LOADING');
  const [leadData, setLeadData] = useState<LeadFormData | null>(null);
  const [salesRep, setSalesRep] = useState<SalesRep | null>(null);
  const [calendarSettings, setCalendarSettings] = useState<CalendarSettings | null>(null);
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  // #3361: the zone the slot strings are wall-clock in, supplied by the API.
  const [slotTimezone, setSlotTimezone] = useState<string | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [calendarUser, setCalendarUser] = useState<{ vendorId: number; userId: string; firstName: string; lastName: string } | null>(null);
  const [timeSlotsAuthId, setTimeSlotsAuthId] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<ITurnstileElement>(null);
  const [activeFeedback, setActiveFeedback] = useState(FEEDBACK_DATA[0]);
  // Inline booking fields — used when calendar-direct flow skips the lead form
  const [bookingName, setBookingName] = useState('');
  const [bookingEmail, setBookingEmail] = useState('');

  const userTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York', []);

  useEffect(() => {
    posthog?.capture('book_demo_viewed', { intent: demoIntent });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check pounce status — if qualification is disabled, skip the form
  // and go straight to the calendar picker. Extracted so handleRetry can re-invoke.
  const checkPounceStatus = useCallback(async () => {
    setScreen('LOADING');
    setErrorMessage('');
    try {
      const res = await fetch(`${BOOK_DEMO_API}/status?vendorId=${VENDOR_ID}`);
      const data = await res.json();

      if (data.success && !data.enabled && data.hasCalendar && data.calendarSettings) {
        // Pounce disabled — skip form, go straight to calendar
        setSalesRep(data.calendarSettings.salesRep || null);
        setCalendarSettings(data.calendarSettings);
        setSlotsLoading(true);
        setScreen('CALENDAR');

        // Fetch calendar data from production to get vendorId + userId
        const calRes = await fetch(`${BOOK_DEMO_API}/calendar`);
        const calData = await calRes.json();

        if (!calRes.ok || !calData.userData) {
          setErrorMessage('Failed to load calendar');
          setScreen('ERROR');
          return;
        }

        const { vendorId: prodVendorId, userId: prodUserId, first_name, last_name } = calData.userData;
        setCalendarUser({ vendorId: prodVendorId, userId: prodUserId, firstName: first_name, lastName: last_name });

        // Fetch time slots from production
        const slotsRes = await fetch(
          `${BOOK_DEMO_API}/time-slots?vendorId=${prodVendorId}&userId=${prodUserId}&timezone=${encodeURIComponent(userTimezone)}`,
        );
        const slotsData = await slotsRes.json();
        setSlotsLoading(false);

        if (slotsRes.ok && slotsData.slots) {
          setSlots(slotsData.slots);
          setSlotTimezone(slotsData.timezone);
          setTimeSlotsAuthId(slotsData.authId);
        } else {
          setErrorMessage('Failed to load available times');
          setScreen('ERROR');
        }
      } else {
        // Pounce enabled or no calendar — show the lead form
        setScreen('LEAD_FORM');
      }
    } catch {
      // Status check failed — fall back to showing the form
      setScreen('LEAD_FORM');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkPounceStatus();
  }, [checkPounceStatus]);

  const handleQualify = async (formData: LeadFormData) => {
    // Bot/abuse protection: the qualify call triggers a notification email on the
    // backend, so it must carry a Turnstile token. The lead-form submit button is
    // disabled until the challenge resolves; this is a defensive guard.
    if (!turnstileToken) return;

    setLeadData(formData);
    setScreen('QUALIFYING');

    posthog?.capture('book_demo_form_submitted', {
      email: formData.email,
      first_name: formData.firstName,
      last_name: formData.lastName,
      company: formData.company,
      num_employees: formData.numEmployees,
      job_title: formData.jobTitle,
      website: formData.website,
      business_model: formData.businessModel,
      funding_stage: formData.fundingStage,
      sales_team_size: formData.salesTeamSize,
      intent: demoIntent,
    });
    posthog?.identify(formData.email, {
      email: formData.email,
      first_name: formData.firstName,
      last_name: formData.lastName,
      company: formData.company,
      num_employees: formData.numEmployees,
      job_title: formData.jobTitle,
      website: formData.website,
      business_model: formData.businessModel,
      funding_stage: formData.fundingStage,
      sales_team_size: formData.salesTeamSize,
    });

    // Flips once /qualify has answered: from then on the backend holds the lead, so
    // a later failure in this handler must not be reported as a failed submission.
    let leadCaptured = false;

    try {
      // Step 1: Qualify lead via local Pounce backend
      const res = await fetch(`${BOOK_DEMO_API}/qualify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendorId: VENDOR_ID,
          turnstileToken,
          email: formData.email,
          formData: withDemoIntent({
            firstname: formData.firstName,
            lastname: formData.lastName,
            company: formData.company,
            numemployees: formData.numEmployees,
            jobtitle: formData.jobTitle,
            // #4620: what Jev needs to tell a SaaS or funded company from a local
            // business. Pounce forwards the whole form to Jev, so no backend change is
            // needed for Jev to read these. Keys match the HubSpot contact properties.
            website: formData.website.trim(),
            business_model: formData.businessModel,
            funding_stage: formData.fundingStage,
            sales_team_size: formData.salesTeamSize,
          }, demoIntent),
        }),
      });

      const data = await res.json();

      // Rate-limited at the proxy edge before the backend was ever reached.
      // Do NOT fall through to the HubSpot fallback — that would create a lead
      // for every throttled submission and defeat the flood protection. The
      // Turnstile token was not consumed (the backend never saw it), so leave
      // it intact for a later retry rather than resetting it.
      if (res.status === 429 || data.rateLimited) {
        posthog?.capture('book_demo_rate_limited');
        setErrorMessage(data.message || 'Too many requests. Please slow down and try again shortly.');
        setScreen('ERROR');
        return;
      }

      // Turnstile tokens are single-use; the backend consumed it verifying this
      // request. Reset so the booking step (and any retry) gets a fresh token.
      turnstileRef.current?.reset();
      setTurnstileToken(null);

      if (!data.success) {
        // Pounce returned an error. The lead was not recorded anywhere, so say so
        // rather than showing a success screen (the old "fallback" did exactly that).
        setErrorMessage(SUBMIT_FAILED_MESSAGE);
        setScreen('ERROR');
        return;
      }

      leadCaptured = true;

      if (!data.qualified) {
        // Pounce holds the lead, has notified the team, and writes it to the CRM
        // server-side (mb-server#5634). Nothing else to do here.
        posthog?.capture('book_demo_not_qualified', { intent: demoIntent });
        setScreen('NOT_QUALIFIED');
        return;
      }

      posthog?.capture('book_demo_qualified', {
        sales_rep_name: data.salesRep?.name,
        intent: demoIntent,
      });
      setSalesRep(data.salesRep);
      setCalendarSettings(data.calendarSettings);

      // Step 2: Fetch calendar data from production to get vendorId + userId
      setSlotsLoading(true);
      setScreen('CALENDAR');

      const calRes = await fetch(`${BOOK_DEMO_API}/calendar`);
      const calData = await calRes.json();

      if (!calRes.ok || !calData.userData) {
        setErrorMessage('Failed to load calendar');
        setScreen('ERROR');
        return;
      }

      const { vendorId: prodVendorId, userId: prodUserId, first_name, last_name } = calData.userData;
      setCalendarUser({ vendorId: prodVendorId, userId: prodUserId, firstName: first_name, lastName: last_name });

      // Step 3: Fetch time slots from production
      const slotsRes = await fetch(
        `${BOOK_DEMO_API}/time-slots?vendorId=${prodVendorId}&userId=${prodUserId}&timezone=${encodeURIComponent(userTimezone)}`,
      );
      const slotsData = await slotsRes.json();
      setSlotsLoading(false);

      if (slotsRes.ok && slotsData.slots) {
        setSlots(slotsData.slots);
          setSlotTimezone(slotsData.timezone);
        setTimeSlotsAuthId(slotsData.authId);
      } else {
        setErrorMessage('Failed to load available times');
        setScreen('ERROR');
      }
    } catch {
      // Before leadCaptured: the backend was unreachable and nothing was recorded.
      // After it: only the calendar / time-slot load failed.
      setErrorMessage(leadCaptured ? CALENDAR_FAILED_MESSAGE : SUBMIT_FAILED_MESSAGE);
      setScreen('ERROR');
    }
  };

  const handleSelectSlot = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setScreen('BOOKING');
    const instant = toVisitorInstant(slot.start, slotTimezone, userTimezone);
    posthog?.capture('book_demo_slot_selected', {
      slot_date: instant.format('YYYY-MM-DD'),
      slot_time: instant.format('h:mm A'),
    });
  };

  const handleBook = async () => {
    // Resolve contact details — prefer leadData from the form, fall back to
    // the inline fields shown on the booking confirmation screen.
    const contactEmail = leadData?.email || bookingEmail.trim();
    const contactName = leadData
      ? `${leadData.firstName} ${leadData.lastName}`.trim()
      : bookingName.trim();

    if (!selectedSlot || !contactEmail || !timeSlotsAuthId) return;

    setBookingLoading(true);
    try {
      // #3361: selectedSlot.start is naive wall-clock in the HOST's zone. Resolve
      // it to the real instant, then re-express it as the visitor's own
      // wall-clock — timeZone below stays userTimezone, so the backend's
      // DateTime.fromISO(date+time, {zone: timeZone}) reconstructs the exact
      // same instant. Submitting the naive string's raw digits (the old
      // behavior) silently booked whatever the browser's local zone happened
      // to compute, which only matched the host's real slot when host and
      // visitor shared a zone.
      const slotDate = toVisitorInstant(selectedSlot.start, slotTimezone, userTimezone);
      const res = await fetch(`${BOOK_DEMO_API}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: slotDate.format('YYYY-MM-DD'),
          time: slotDate.format('HH:mm'),
          email: contactEmail,
          name: contactName,
          timeZone: userTimezone,
          authId: timeSlotsAuthId,
          topic: `Demo for ${leadData?.company || contactEmail}`,
          vendorId: calendarUser?.vendorId,
          turnstileToken,
          // Mark this as a book-demo (pounce) booking so the backend flips the
          // pounce_events.meeting_booked analytics mirror and sends a booking-confirmed
          // notification (the lead form already created the qualify-stage event).
          bookingContext: {
            source: 'pounce',
            formData: withDemoIntent({
              firstname: leadData?.firstName,
              lastname: leadData?.lastName,
              company: leadData?.company,
            }, demoIntent),
          },
        }),
      });

      const data = await res.json();
      setBookingLoading(false);

      turnstileRef.current?.reset();
      setTurnstileToken(null);

      if (res.ok) {
        posthog?.capture('book_demo_confirmed', {
          sales_rep_name: salesRep?.name,
          slot_date: slotDate.format('YYYY-MM-DD'),
          slot_time: slotDate.format('h:mm A'),
        });
        setScreen('SUCCESS');
      } else {
        posthog?.capture('book_demo_error', { error_message: data.error || 'Failed to book meeting' });
        setErrorMessage(data.error || 'Failed to book meeting');
        setScreen('ERROR');
      }
    } catch {
      setBookingLoading(false);
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      posthog?.capture('book_demo_error', { error_message: 'Failed to connect. Please try again.' });
      setErrorMessage('Failed to connect. Please try again.');
      setScreen('ERROR');
    }
  };

  const handleReschedule = () => {
    setSelectedSlot(null);
    setScreen('CALENDAR');
    posthog?.capture('book_demo_rescheduled');
  };

  const handleRetry = () => {
    setErrorMessage('');
    // Re-run pounce status check so the calendar-direct path is recoverable
    checkPounceStatus();
  };

  const handleNextFeedback = () => {
    const currentIndex = FEEDBACK_DATA.indexOf(activeFeedback);
    setActiveFeedback(FEEDBACK_DATA[(currentIndex + 1) % FEEDBACK_DATA.length]);
  };

  const handleBackFeedback = () => {
    const currentIndex = FEEDBACK_DATA.indexOf(activeFeedback);
    setActiveFeedback(FEEDBACK_DATA[(currentIndex - 1 + FEEDBACK_DATA.length) % FEEDBACK_DATA.length]);
  };

  const renderScreen = () => {
    switch (screen) {
      case 'LOADING':
        return (
          <div className='flex flex-col items-center justify-center py-16'>
            <Spinner className='w-8 h-8' />
          </div>
        );

      case 'LEAD_FORM':
        return (
          <LeadForm
            onSubmit={handleQualify}
            isLoading={false}
            submitDisabled={!turnstileToken}
            footer={(
              <Turnstile
                ref={turnstileRef}
                onSuccess={(token) => setTurnstileToken(token)}
                onStateChange={(state) => {
                  if (state === 'expired' || state === 'error') setTurnstileToken(null);
                }}
              />
            )}
          />
        );

      case 'QUALIFYING':
        return (
          <div className='flex flex-col items-center justify-center py-16'>
            <Spinner className='w-8 h-8' />
            <p className='text-sm text-gray-500 mt-4'>Please wait a moment...</p>
          </div>
        );

      case 'CALENDAR':
        return (
          <div>
            <button
              onClick={() => setScreen('LEAD_FORM')}
              className='text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1'
            >
              <ChevronLeft size={16} /> Back
            </button>
            <CalendarPicker
            hostTimezone={slotTimezone} slots={slots} onSelectSlot={handleSelectSlot} isLoading={slotsLoading} />
          </div>
        );

      case 'BOOKING': {
        // When pounce is disabled we skip the lead form, so leadData is null.
        // Show inline name + email fields so the user can provide them before booking.
        const needsContactInfo = !leadData;
        const canBook = turnstileToken && (leadData || bookingEmail.trim());

        return selectedSlot ? (
          <div className='flex flex-col gap-6'>
            <button
              onClick={() => setScreen('CALENDAR')}
              className='text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1'
            >
              <ChevronLeft size={16} /> Back to calendar
            </button>
            <div className='text-center'>
              <p className='font-medium text-lg'>Confirm your booking</p>
              {salesRep && <p className='text-sm text-gray-500 mt-1'>Meeting with {salesRep.name}</p>}
            </div>
            <div className='p-4 border border-gray-200 rounded-lg bg-gray-50'>
              <div className='flex items-center gap-3'>
                <Clock4 className='text-gray-500' size={20} />
                <div>
                  {/* #3361: must match the CalendarPicker grid the visitor just
                      chose from and what handleBook actually submits — all three
                      now resolve through the same host-zone-aware parse. */}
                  <p className='font-medium text-sm'>{toVisitorInstant(selectedSlot.start, slotTimezone, userTimezone).format('dddd, MMMM D, YYYY')}</p>
                  <p className='text-sm text-gray-600'>
                    {toVisitorInstant(selectedSlot.start, slotTimezone, userTimezone).format('h:mm A')} - {toVisitorInstant(selectedSlot.end, slotTimezone, userTimezone).format('h:mm A')}{' '}
                    ({userTimezone})
                  </p>
                </div>
              </div>
            </div>
            {needsContactInfo && (
              <div className='flex flex-col gap-3'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Your Name</label>
                  <input
                    type='text'
                    value={bookingName}
                    onChange={(e) => setBookingName(e.target.value)}
                    className='w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                    placeholder='Full name'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Email *</label>
                  <input
                    type='email'
                    value={bookingEmail}
                    onChange={(e) => setBookingEmail(e.target.value)}
                    className='w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                    placeholder='you@company.com'
                  />
                </div>
              </div>
            )}
            <Turnstile
              ref={turnstileRef}
              onSuccess={(token) => setTurnstileToken(token)}
            />
            <Button onClick={handleBook} disabled={bookingLoading || !canBook} className='w-full'>
              {bookingLoading ? <Spinner className='w-5 h-5' /> : 'Confirm Booking'}
            </Button>
          </div>
        ) : null;
      }

      case 'SUCCESS':
        return salesRep && selectedSlot ? (
          <SuccessScreen
            salesRep={salesRep}
            slot={selectedSlot}
            onReschedule={handleReschedule}
            hostTimezone={slotTimezone}
          />
        ) : null;

      case 'NOT_QUALIFIED':
        return (
          <div className='flex flex-col items-center py-12 text-center'>
            <div className='w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6'>
              <Mail className='text-green-600' size={28} />
            </div>
            <p className='font-semibold text-lg'>Thank you, {leadData?.firstName}!</p>
            <p className='text-gray-600 mt-2 max-w-sm'>
              We&apos;ve received your request. A member of our team will reach out shortly to schedule your demo.
            </p>
          </div>
        );

      case 'ERROR':
        return (
          <div className='flex flex-col items-center py-12 text-center'>
            <TriangleAlert className='text-amber-500 mb-4' size={48} />
            <p className='font-semibold text-lg'>Something went wrong</p>
            <p className='text-gray-600 mt-2'>{errorMessage}</p>
            <Button className='mt-6' onClick={handleRetry}>
              Try again
            </Button>
          </div>
        );
    }
  };

  return (
    <section className='book-demo__section relative pt-12 pb-20'>
      <div className='container-fluid px-4 sm:px-7.5'>
        {/* Desktop padding only: at phone width 70px a side left the lead form ~130px wide. */}
        <div className='flex gap-8 xl:gap-15 py-8 px-4 sm:px-8 xl:py-15 xl:px-17.5 bg-[#E6EDFF]/[51%] rounded-[20px] flex-col xl:flex-row'>
          <FadeIn className='w-full xl:w-[43%]' variant='right' delay={0.3}>
            <p className='text-sectionTitleBase font-medium text-black text-center xl:text-start'>
              Build the right <span className='text-blue-500'>GTM motion</span> with a MarketBetter expert
            </p>
            <p className='pt-5.5 text-black text-sectionDesc text-center xl:text-start'>
              Get a hands-on walkthrough tailored to your team, stack, and highest-value workflows.
            </p>

            <div className='mt-6'>
              <Image
                src='https://assets.marketbetter.ai/images/team/adam_grant.jpeg'
                alt='Product Expert'
                className='w-full rounded-[20px] border-[6px] border-white shadow-2xl object-cover'
                width={400}
                height={300}
              />
            </div>
          </FadeIn>
          <FadeIn
            variant='left'
            delay={0.3}
            className='w-full xl:w-[57%] bg-white rounded-2xl shadow-drop py-8 px-6 xl:min-h-[669px] relative'
          >
            <p className='text-sectionDesc text-black text-center font-semibold pb-8'>
              {screen === 'LOADING' && 'Book a demo with the marketbetter.ai team'}
              {screen === 'LEAD_FORM' && 'Book a demo with the marketbetter.ai team'}
              {screen === 'QUALIFYING' && 'Book a demo with the marketbetter.ai team'}
              {screen === 'CALENDAR' && 'Select a time'}
              {screen === 'BOOKING' && 'Almost there!'}
              {screen === 'SUCCESS' && 'Booking confirmed!'}
              {screen === 'NOT_QUALIFIED' && 'Request received!'}
              {screen === 'ERROR' && 'Oops!'}
            </p>
            {renderScreen()}
          </FadeIn>
        </div>

        <div className='px-20 pt-30 flex items-center gap-20 relative flex-col xl:flex-row'>
          <FadeIn variant='right' delay={0.5} className='w-full xl:w-[47%]'>
            <p className='text-sectionTitleBase font-medium text-black pb-25 text-center xl:text-start'>
              Powering revenue teams with AI-driven efficiency
            </p>
            <div className='grid grid-cols-2 gap-x-10 2xl:gap-x-22.5 gap-y-12'>
              {INFORMATION_LIST.map((item, index) => (
                <div key={index} className='flex items-center gap-2.5'>
                  <div className='h-full w-1 bg-green-600 rounded-xl' />
                  <div>
                    <p className='text-sectionTitleBase font-medium text-black'>{item.title}</p>
                    <p className='text-xl text-black pt-1'>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn variant='left' delay={0.5} className='w-full xl:w-[53%] flex justify-end'>
            <FeedbackCard
              className='max-w-[627px] min-h-[514px] flex flex-col justify-between'
              onNext={handleNextFeedback}
              onBack={handleBackFeedback}
              feedback={activeFeedback}
            />
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default BookDemo;
